import { useState, useCallback } from "react";
import { Upload, Camera, Image as ImageIcon } from "lucide-react";
import { Button } from "../ui/button";

export default function ImageUploader({ onImageSelect, isAnalyzing }) {
    const [dragActive, setDragActive] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);

    const handleDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const file = e.dataTransfer.files[0];
        if (file && file.type.startsWith("image/")) {
            processFile(file);
        }
    }, []);

    const handleFileInput = (e) => {
        const file = e.target.files[0];
        if (file) {
            processFile(file);
        }
    };

    const processFile = (file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            setPreviewUrl(reader.result);
        };
        reader.readAsDataURL(file);
        onImageSelect(file);
    };

    return (
        <div className="w-full">
            <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`relative border-2 border-dashed rounded-xl transition-all duration-300 overflow-hidden ${dragActive
                    ? "border-amber-500 bg-amber-500/5 scale-[1.02]"
                    : "border-gray-800 bg-zinc-900/50 hover:border-gray-700"
                    }`}
            >
                {previewUrl ? (
                    <div className="relative">
                        <img
                            src={previewUrl}
                            alt="Chart preview"
                            className="w-full h-auto rounded-lg"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent flex items-end p-6">
                            <Button
                                variant="secondary"
                                onClick={() => document.getElementById("file-input").click()}
                                disabled={isAnalyzing}
                                className="bg-white text-black hover:bg-gray-100 border border-amber-500/20"
                            >
                                <Upload className="w-4 h-4 mr-2" />
                                Change Image
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="p-12 text-center">
                        <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-amber-500/10 to-yellow-600/10 rounded-xl flex items-center justify-center backdrop-blur-sm border border-amber-500/20">
                            <ImageIcon className="w-12 h-12 text-amber-500" />
                        </div>
                        <h3 className="text-2xl font-semibold text-white mb-2">
                            Upload Chart Screenshot
                        </h3>
                        <p className="text-gray-400 mb-6 max-w-sm mx-auto">
                            Drag and drop your forex chart image here, or click to browse
                        </p>
                        <Button
                            onClick={() => document.getElementById("file-input").click()}
                            className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black font-semibold shadow-lg shadow-amber-500/25 border border-amber-400/20"
                        >
                            <Upload className="w-4 h-4 mr-2" />
                            Select Image
                        </Button>
                        <p className="text-xs text-gray-600 mt-4">
                            Supports: JPG, PNG, WebP
                        </p>
                    </div>
                )}
                <input
                    id="file-input"
                    type="file"
                    accept="image/*"
                    onChange={handleFileInput}
                    className="hidden"
                />
            </div>
        </div>
    );
}
